import { Box, Button, IconButton, Typography, Drawer, type SxProps } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import { isValidElement, type MouseEvent, useEffect, useMemo, useState } from "react";
import { FilterFormBase, FilterFormProps, useListFilterContext } from "react-admin";
import { FormProvider, useForm } from "react-hook-form";
import { Column } from "../misc/Column";
import { Row } from "../misc/Row";
import { useThemeColor } from "../hooks/useThemeColor";
import { FILTER_MODE_SUFFIX } from "../backoffice.utils";

export function MoreFiltersButton(props: { filters: FilterFormProps["filters"]; sx?: SxProps }) {
  const { filters, sx } = props;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { filterValues, setFilters, displayedFilters: contextDisplayedFilters } = useListFilterContext();
  const primaryColor = useThemeColor("primary");
  const secondaryFiltersSources = useMemo(() => {
    const filterSources: string[] = [];
    filters?.forEach((filterInput) => {
      if (!isValidElement(filterInput) || !filterInput.props?.source) {
        return;
      }
      filterSources.push(filterInput.props.source);
    });
    return filterSources;
  }, [filters]);
  const selectedSecondaryFiltersCount = useMemo(() => {
    const values = (filterValues ?? {}) as Record<string, unknown>;
    return secondaryFiltersSources.reduce((count, filterSource) => {
      return count + (isSelectedFilter(values[filterSource]) ? 1 : 0);
    }, 0);
  }, [filterValues, secondaryFiltersSources]);
  const handleResetSecondaryFilters = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const currentValues = (filterValues ?? {}) as Record<string, unknown>;
    const clearedFilterValues = Object.keys(currentValues).reduce<Record<string, unknown>>((acc, key) => {
      if (
        !secondaryFiltersSources.includes(key) &&
        !secondaryFiltersSources.some((source) => key === `${source}${FILTER_MODE_SUFFIX}`)
      ) {
        acc[key] = currentValues[key];
      }
      return acc;
    }, {});

    setFilters(clearedFilterValues, contextDisplayedFilters);
  };
  const hasSelectedSecondaryFilters = selectedSecondaryFiltersCount > 0;

  return (
    <>
      <Button
        onClick={() => setDrawerOpen(true)}
        variant="text"
        startIcon={<FilterListIcon sx={{ ml: 0.5 }} />}
        endIcon={
          hasSelectedSecondaryFilters ? (
            <IconButton
              component="span"
              onClick={handleResetSecondaryFilters}
              size="small"
              aria-label="Filter zurücksetzen"
              sx={{
                mr: 0.5,
                "&:hover": {
                  backgroundColor: primaryColor.light,
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : undefined
        }
        sx={{
          width: "max-content",
          minWidth: "max-content",
          borderRadius: 40,
          fontSize: { xs: "1rem", md: "0.875rem" },
          height: "37px",
          ...(hasSelectedSecondaryFilters
            ? {
                backgroundColor: primaryColor.main,
                color: primaryColor.contrastText,
                "& .MuiSvgIcon-root": {
                  color: primaryColor.contrastText,
                },
                "&:hover": {
                  backgroundColor: primaryColor.main,
                },
              }
            : {
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }),
          ...sx,
        }}
      >
        {`Weitere Filter${selectedSecondaryFiltersCount > 0 ? ` (${selectedSecondaryFiltersCount})` : "\u00A0"}`}
      </Button>
      <FilterDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} filters={filters} />
    </>
  );
}

function FilterDrawer(props: { open: boolean; onClose: () => void; filters: FilterFormProps["filters"] }) {
  const { open, onClose, filters } = props;
  const { filterValues, setFilters, displayedFilters: contextDisplayedFilters } = useListFilterContext();

  const displayedFilters = useMemo(() => {
    // Only secondary filter inputs should be displayed in the drawer
    const visibleFilters: Record<string, boolean> = {};
    filters?.forEach((filterInput) => {
      if (!isValidElement(filterInput) || !filterInput.props?.source) {
        return;
      }
      visibleFilters[filterInput.props.source] = true;
    });
    return visibleFilters;
  }, [filters]);
  const secondarySources = useMemo(() => Object.keys(displayedFilters), [displayedFilters]);

  const form = useForm({
    defaultValues: filterValues,
  });

  useEffect(() => {
    const formValues = { ...filterValues };
    secondarySources.forEach((source) => {
      formValues[source] ??= Array.isArray(form.getValues(source)) ? [] : "";
      formValues[`${source}${FILTER_MODE_SUFFIX}`] ??= "included";
    });
    form.reset(formValues);
  }, [filterValues, form, secondarySources]);

  useEffect(() => {
    const subscription = form.watch((values, { name }) => {
      if (!name) {
        return;
      }
      setFilters(values, contextDisplayedFilters);
    });
    return () => subscription.unsubscribe();
  }, [form, contextDisplayedFilters, setFilters]);

  const handleResetSecondaryFilters = () => {
    const currentValues = (filterValues ?? {}) as Record<string, unknown>;
    const clearedFilterValues = Object.keys(currentValues).reduce<Record<string, unknown>>((acc, key) => {
      if (!displayedFilters[key] && !secondarySources.some((source) => key === `${source}${FILTER_MODE_SUFFIX}`)) {
        acc[key] = currentValues[key];
      }
      return acc;
    }, {});
    const clearedFormValues = { ...clearedFilterValues } as Record<string, unknown>;
    secondarySources.forEach((source) => {
      clearedFormValues[source] = Array.isArray(form.getValues(source)) ? [] : "";
      clearedFormValues[`${source}${FILTER_MODE_SUFFIX}`] = "included";
    });
    setFilters(clearedFilterValues, displayedFilters);
    form.reset(clearedFormValues);
  };

  return (
    <Drawer
      anchor="right"
      PaperProps={{
        sx: {
          boxSizing: "border-box",
          width: { xs: "100vw", sm: "350px" },
          maxWidth: "100vw",
          top: 0,
          height: "100dvh",
          maxHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          p: 3,
          borderRadius: { xs: 0, sm: "8px 0 0 8px" },
          boxShadow: "-10px 0 10px 0 rgba(0, 0, 0, 0.06)",
        },
      }}
      slotProps={{
        backdrop: {
          invisible: true,
        },
      }}
      open={open}
      onClose={onClose}
    >
      <Column
        sx={{
          height: "100%",
          minHeight: 0,
          "& .RaFilterForm-root": {
            flexDirection: "column",
          },
          "& .RaFilterForm-filterFormInput": {
            flexBasis: "100%",
            maxWidth: "100%",
            marginBottom: "16px",
            width: "100%",
          },
          "& .RaFilterForm-filterFormInput > .MuiFormControl-root, & .RaFilterForm-filterFormInput > .MuiAutocomplete-root":
            {
              width: "100% !important",
            },
          "& .RaFilterFormInput-spacer": {
            display: "none",
          },
        }}
      >
        <Row justifyContent="space-between" mb={2} alignItems="start">
          <Column mt={1}>
            <Typography variant="h6">Weitere Filter</Typography>
            <Button
              onClick={handleResetSecondaryFilters}
              variant="text"
              disableRipple
              sx={{
                fontSize: 14,
                p: 0,
                justifyContent: "flex-start",
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              Zurücksetzen
            </Button>
          </Column>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Row>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            pr: 0.5,
            pb: 3,
          }}
        >
          <FormProvider {...form}>
            <FilterFormBase filters={filters ?? []} displayedFilters={displayedFilters} />
          </FormProvider>
        </Box>
      </Column>
    </Drawer>
  );
}

function isSelectedFilter(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === "string") {
    return value.trim().length > 0;
  }
  return true;
}
